import axios from 'axios';
import {ENDPOINT_URL, GET_VLAN_POOL_LIST} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a VLAN Pool row identifier.
 *
 * Each Global Profile names its key differently - pool_id here, plain id
 * on ACS Schedules, nat_config_id and profile_id elsewhere - so this is
 * per resource rather than shared.
 *
 * @param {object} item Canvas VLAN Pool row
 * @return {*} identifier
 */
function rowId(item) {
	return item.pool_id ?? item.id;
}

/**
 * Normalize a Canvas VLAN Pool row.
 * @param {object} item Canvas VLAN Pool row
 * @return {object|null} agent candidate
 */
function normalizeRow(item) {
	const id = rowId(item);
	const name = item.profile_name ?? item.name;
	if (id == null || !name) return null;
	return {
		entity_type: 'vlan_pool',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
	};
}

/**
 * Resolve VLAN Pool candidates.
 *
 * Canvas matches ?search= against the profile name server-side, which is
 * the same list the table itself is filtered by, so a name the agent finds
 * is a name the person can see.
 *
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveVlanPools(params) {
	const query = String(params.query || '').trim();
	// `browse` lists the scope instead of matching a name, so a miss can
	// answer with the profiles that do exist.
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_VLAN_POOL_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('vlan_pool', rows, rowId);
		return {
			candidates: rows.map(normalizeRow).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('vlan_pool', rows, rowId);
	return {candidates};
}

/**
 * Return a retained VLAN Pool row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedVlanPool(entityId) {
	return getResolvedEntity('vlan_pool', entityId);
}
